import { t } from 'i18next';

import type { ApiErrorResponse } from '~/api/Requests';
import { notificationService } from '~/services/NotificationService';

export class OrderErrorHandled extends Error {
  constructor() {
    super('Order error handled');
    this.name = 'OrderErrorHandled';
  }
}

const ORDER_ERROR_CODES = [
  'LESS_MIN_AMOUNT',
  'GREATER_MAX_AMOUNT',
  'CUSTOMER_PHONE_REQUIRED',
  'INVALID_PHONE_FORMAT',
  'CONSTRAINT_VIOLATION',
] as const;

type OrderErrorCode = (typeof ORDER_ERROR_CODES)[number];

function isKnownErrorCode(code: string): code is OrderErrorCode {
  return ORDER_ERROR_CODES.includes(code as OrderErrorCode);
}

/**
 * Attempts to handle an order error by errorCode.
 * Returns true if the error was handled, false otherwise (fallback to old logic).
 */
export function handleOrderError(responseData: ApiErrorResponse): boolean {
  const errorCode = responseData.data?.errorCode;
  if (!errorCode || !isKnownErrorCode(errorCode)) {
    return false;
  }

  const title = t(`orderErrors:${errorCode}`);
  const descriptionKey = `orderErrors:${errorCode}_description`;
  const descriptionParams: Record<string, unknown> = {};

  if (errorCode === 'LESS_MIN_AMOUNT') {
    descriptionParams.minAmount = responseData.data?.minAmount;
  } else if (errorCode === 'GREATER_MAX_AMOUNT') {
    descriptionParams.maxAmount = responseData.data?.maxAmount;
  }

  const description = t(descriptionKey, descriptionParams);

  notificationService.error(title, description);
  return true;
}
